// Password policy shared by set-member-password (server, the authority) and
// SetMemberPasswordDialog (fast feedback). #136.

/**
 * The user pool's password policy. amplify/auth/resource.ts sets no
 * passwordPolicy, so Amplify's defaults apply (confirmed on the dev pool with
 * describe-user-pool, 14 Oct 2026). amplify/functions/set-member-password/rules.test.ts fails if auth/resource.ts
 * starts overriding it, so the two can't drift silently.
 */
export const PASSWORD_POLICY = {
  minLength: 8,
  maxLength: 256, // Cognito's own maximum
  requireUppercase: true,
  requireLowercase: true,
  requireNumbers: true,
  requireSymbols: true,
} as const;

/** The rules in plain English, for the dialog and for error messages. */
export const PASSWORD_RULES_TEXT =
  'At least 8 characters, with an uppercase letter, a lowercase letter, a number and a symbol.';

export type PasswordCheck = { ok: true } | { ok: false; message: string };

/** Checks a password against PASSWORD_POLICY. Never echoes the password. */
export function validatePasswordAgainstPolicy(password: unknown): PasswordCheck {
  if (typeof password !== 'string' || password.length === 0) {
    return { ok: false, message: 'Enter a new password.' };
  }
  if (password.length < PASSWORD_POLICY.minLength) {
    return { ok: false, message: `The password must be at least ${PASSWORD_POLICY.minLength} characters.` };
  }
  if (password.length > PASSWORD_POLICY.maxLength) {
    return { ok: false, message: `The password must be at most ${PASSWORD_POLICY.maxLength} characters.` };
  }
  if (password !== password.trim()) {
    return { ok: false, message: 'The password cannot start or end with a space.' };
  }
  if (PASSWORD_POLICY.requireUppercase && !/[A-Z]/.test(password)) {
    return { ok: false, message: 'The password needs an uppercase letter.' };
  }
  if (PASSWORD_POLICY.requireLowercase && !/[a-z]/.test(password)) {
    return { ok: false, message: 'The password needs a lowercase letter.' };
  }
  if (PASSWORD_POLICY.requireNumbers && !/[0-9]/.test(password)) {
    return { ok: false, message: 'The password needs a number.' };
  }
  if (PASSWORD_POLICY.requireSymbols && !/[^A-Za-z0-9\s]/.test(password)) {
    return { ok: false, message: 'The password needs a symbol, for example ! or #.' };
  }
  return { ok: true };
}
